import { nextTick } from '@patrianna/shared-utils'
import { intlnumberFormat } from '@patrianna/shared-utils/numbers'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { buyShopOffer } from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import cx from 'classnames'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'

import Button from 'components/Button'
import Img from 'components/Img'
import { IS_BRAND_PULSZ } from 'config/brands'
import content from 'prismic-translates/common.json'
import { trackSPPlacementInteracted } from 'src/analyticActions/common/trackPaymentSnowplow'
import { useAppDispatch } from 'src/store/hooks'

import OfferOldPrice from './components/OfferOldPrice'
import OfferTagsHeader from './components/OfferTagsHeader'
import OfferTagsWrapper from './components/OfferTagsWrapper'
import classes from './styles/largeStyles.module.scss'
import type { ShopTileProps } from './types'

const t = getTranslationFromJson([content])

function ShopTileLarge(props: ShopTileProps) {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { offer, snowplowPayFlow, snowplowPlacementName } = props
  const scActualPrice = offer.sweepstakeFirstOffer || offer.sweepstakeMoney
  const gcActualPrice = offer.goldFistOffer || offer.goldMoney

  const showFreeSpins = !!offer.freeSpins

  const scBenefits = Boolean(scActualPrice && props.sweepstakeEnabled)
  const specialOfferImgUrl = offer.specialOfferUrl || `${process.env.IMG_ORIGIN}/shop/coins-v2.png`
  const offerTags = offer?.tags

  const tagsString = useMemo(() => offerTags?.join(', '), [offerTags])

  const buyOfferHandler = () => {
    void nextTick(() => {
      trackSPPlacementInteracted({
        paymentFlow: snowplowPayFlow,
        placementName: snowplowPlacementName,
        offer,
        interactionName: intlnumberFormat(offer.price, 'en-US', { style: 'currency', currency: 'USD' }),
        placementTrigger: 'click_store_button',
      })
      dispatch(
        buyShopOffer({
          offer,
          feature: 'in_store',
          routerPush: router.push,
          snowplowPayFlow,
        }),
      )
    })

    props?.trackButtonClick?.()
  }

  return (
    <OfferTagsWrapper enabled={!!offerTags.length} data-tags={tagsString}>
      <div className={cx('mt-paper', classes.wrapper)} data-testid={props.dataTestId || 'store-offer-item'}>
        <OfferTagsHeader tags={offerTags} />
        <div className={cx(classes.root)}>
          <div className={classes.coinsCol}>
            <picture>
              <Img
                src={specialOfferImgUrl}
                className={classes.icon}
                alt={offer.title}
                fallbackImg={specialOfferImgUrl}
              />
            </picture>
            <div className={classes.coins}>
              <p className={classes.newCount} data-testid='store-offer-gc-amount'>
                <b>
                  {t('gold_coins_gc')} {intlnumberFormat(gcActualPrice)}
                </b>
              </p>

              {offer.goldFistOffer && (
                <p className={classes.oldCount}>
                  {t('was')}
                  <span className={classes.disabledText}>{intlnumberFormat(offer.goldMoney)}</span>
                </p>
              )}
            </div>
          </div>

          <div className={classes.benefitsTitle}>{t('free_purchase_benefits')}</div>

          {offer.vipPoints && (
            <div className={classes.vipCoin}>
              <span className={classes.vipPointsValue}>
                {intlnumberFormat(offer.vipPoints, 'en-US', { style: 'decimal' })}{' '}
              </span>

              {IS_BRAND_PULSZ ? t('pulsz_points') : t('vip_points')}
            </div>
          )}

          <div className={classes.goldenKey}>
            <img src={`${process.env.IMG_ORIGIN}/icons/key.png`} alt='golden key' />
            <span>{t('golden_key')}</span>
          </div>

          {scBenefits ? (
            <div className={classes.sweepstakeBonus}>
              <img
                src={`${process.env.IMG_ORIGIN}/icons/sc-icon.png`}
                className={classes.currencyIcon}
                alt='sweepstakes icon'
              />
              <span>
                {` ${'SC'} `}
                {intlnumberFormat(scActualPrice, 'en-US', { style: 'decimal' })}
              </span>{' '}
            </div>
          ) : (
            <span />
          )}

          {showFreeSpins ? (
            <div className={classes.freeSpins}>
              <img
                src={`${process.env.IMG_ORIGIN}/icons/free-spins.png`}
                className={classes.currencyIcon}
                alt='free spins icon'
              />
              <span>
                {offer.freeSpins} {t('free_spins')}
              </span>
            </div>
          ) : (
            <span />
          )}

          <Button
            data-test={`pay-button-${offer.code}`}
            onClick={buyOfferHandler}
            className={classes.button}
            disabled={props.disabled}
          >
            {intlnumberFormat(offer.price, 'en-US', { style: 'currency', currency: 'USD' })}
            <OfferOldPrice oldPrice={offer.oldPrice} />
          </Button>
        </div>
      </div>
    </OfferTagsWrapper>
  )
}

export default ShopTileLarge
